import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@/components/Button';
import RarityBadge from '@/components/RarityBadge';
import ConfirmDialog from '@/components/ConfirmDialog';
import questionIcon from '@/assets/question.svg';
import { formatCurrency } from '@/utils/formatUtils';
import { getProductThumbnailUrl } from '@/utils/imageUtils';
import { trackEvent } from '@/utils/googleAnalytics';
import OfficialTag from '@/components/OfficialTag';
import OrderRewardBanner from '@/components/OrderRewardBanner';
import { useMetadata } from '@/hooks/useMetadata';

const OrderSection = ({ card, stats, canPay, purchaseDetails, previewData, onPayClick, isProcessingPayment = false }) => {
  const { t } = useTranslation();
  const [helpDialog, setHelpDialog] = useState({ isOpen: false, type: null });
  const { data: metadata, isLoading: isMetadataLoading } = useMetadata();
  
  // Check if orderRewardTips exists to conditionally render the wrapper div
  const hasOrderRewardTips = !isMetadataLoading && metadata?.orderRewardTips?.icon?.url;

  // Use price from preview data if available, otherwise fallback to URL parameters or stats
  const subtotal = previewData?.monetary?.price || purchaseDetails?.price || stats?.lowestPriceOnSale || 0;
  const serviceFee = previewData?.monetary?.serviceFee || 0;
  const shipping = previewData?.monetary?.shippingFee || 0;
  const inspectionFee = previewData?.monetary?.verificationFee || 0;
  const total = previewData?.monetary?.total || (subtotal + serviceFee + shipping + inspectionFee);

  // Determine if we have specific PSA grade or bundled quantity
  const hasPsaGrade = !!purchaseDetails?.psaGrade;
  const hasCondition = !!purchaseDetails?.condition;
  const hasBundledQuantity = !!purchaseDetails?.bundledQuantity;

  const handlePay = () => {
    trackEvent('pay_click');

    if (onPayClick && canPay) {
      onPayClick();
    }
  };

  const showHelpDialog = (type) => {
    setHelpDialog({ isOpen: true, type });
  };

  const closeHelpDialog = () => {
    setHelpDialog({ isOpen: false, type: null });
  };

  // Determine if this is a card box purchase
  const isCardBox = !!purchaseDetails?.bundledQuantity;

  // Get help content based on dialog type
  const helpContent = (() => {
    switch (helpDialog.type) {
      case 'shipping':
        return {
          title: t('purchase.order.shipping'),
          description: isCardBox
            ? t('purchase.order.help.shipping.card_box')
            : t('purchase.order.help.shipping.single_card')
        };
      case 'inspection_fee':
        return {
          title: t('purchase.order.inspection_fee'),
          description: (
            <div>
              <div>{t('purchase.order.help.inspection_fee.title')}</div>
              <div className="mt-2">{t('purchase.order.help.inspection_fee.description')}</div>
            </div>
          )
        };
      case 'handling_fee':
        return {
          title: t('purchase.order.handling_fee'),
          description: t('purchase.order.help.handling_fee')
        };
      default:
        return { title: '', description: '' };
    }
  })();

  return (
    <div className="w-[465px]">
      <div className="bg-white rounded-md px-6">
        <div className="py-6">
          <h2 className="text-lg font-bold text-[#111111]">{t('purchase.order.title')}</h2>
        </div>

        <div className="pb-8 flex gap-6">
          <div className="w-[167px] h-[167px] bg-white rounded flex items-center justify-center">
            <img 
              src={getProductThumbnailUrl(card.images?.[0]?.url)} 
              alt={card.localizedName}
              className="w-full h-full object-contain mix-blend-darken"
            />
          </div>
          <div className="flex-1 space-y-6">
            <div>
              <div className="min-w-0">
                {(previewData?.monetary?.isOfficial) && (
                  <OfficialTag className="align-middle mr-1.5" />
                )}
                <h3 className="inline align-middle text-base whitespace-normal break-words min-w-0">
                  {card.localizedRawName || card.localizedName || card.name}
                  {isCardBox ? '' : (card.rarityInfo?.sub?.name ? `(${card.rarityInfo.sub.name})` : '')}
                </h3>
              </div>
              <div className="flex flex-wrap items-center gap-1 mt-1">
                {!isCardBox && card.rarityInfo?.main && (
                  <RarityBadge rarity={card.rarityInfo.main} />
                )}
                {(card.set?.code || (card.rawCode && !isCardBox)) && (
                  <div className="h-[18px] px-1 bg-[#f4f4f4] rounded-[3px] outline outline-[0.50px] outline-offset-[-0.50px] outline-[#dddddd] flex justify-start items-center gap-2.5">
                    <div className="justify-start text-[#111111] text-sm font-normal leading-[14px]">
                      {isCardBox ? card.set?.code : (card.set?.code && card.rawCode ? `${card.set.code} ${card.rawCode}` : card.set?.code || card.rawCode)}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="space-y-[5px]">
              {hasPsaGrade && (
                <div className="flex gap-2">
                  <span className="w-[100px] text-sm text-[#aaaaaa]">{t('purchase.order.psa_grade')}</span>
                  <span className="text-sm text-[#111111] flex-1">
                    {purchaseDetails.psaGrade.replace('PSA_', 'PSA ')}
                  </span>
                </div>
              )}
              {hasCondition && (
                <div className="flex gap-2">
                  <span className="w-[100px] text-sm text-[#aaaaaa]">{t('me.profile.info.condition')}</span>
                  <span className="text-sm text-[#111111] flex-1">
                    {t(`sell.product_info.conditions.${purchaseDetails.condition.toLowerCase()}`)}
                  </span>
                </div>
              )}
              {hasBundledQuantity && (
                <div className="flex gap-2">
                  <span className="w-[100px] text-sm text-[#aaaaaa]">{t('purchase.order.quantity')}</span>
                  <span className="text-sm text-[#111111] flex-1">
                    {purchaseDetails.bundledQuantity} {card?.boxType === 'PACK' ? t('box.pack') : t('box.box_unit')}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {previewData && (
          <div className="border-t border-[#f4f4f4]">
            <div className="py-6">
              <h3 className="text-lg font-bold text-[#111111]">{t('purchase.order.payment_details')}</h3>
            </div>
            
            <div className="space-y-4">
              <div className="flex justify-between">
                <div className='flex gap-1 items-center'>
                  <span className="text-sm text-[#111111]">{t('purchase.order.subtotal')}</span>
                </div>
                <span className="text-sm font-medium text-[#111111]">{formatCurrency(subtotal)}</span>
              </div>
              {serviceFee > 0 && (<div className="flex justify-between">
                <div className='flex gap-1 items-center'>
                  <span className="text-sm text-[#111111]">{t('purchase.order.handling_fee')}</span>
                  {/* <button onClick={() => showHelpDialog('handling_fee')} className="cursor-pointer">
                    <img src={questionIcon} className="w-4 h-4" alt="help" />
                  </button> */}
                </div>
                <span className="text-sm font-medium text-[#111111]">{formatCurrency(serviceFee)}</span>
              </div>)}
              <div className="flex justify-between">
                <div className='flex gap-1 items-center'>
                  <span className="text-sm text-[#111111]">{t('purchase.order.shipping')}</span>
                  <button onClick={() => showHelpDialog('shipping')} className="cursor-pointer">
                    <img src={questionIcon} className="w-4 h-4" alt="help" />
                  </button>
                </div>
                <span className="text-sm font-medium text-[#111111]">{formatCurrency(shipping)}</span>
              </div>
              <div className="flex justify-between">
                <div className='flex gap-1 items-center'>
                  <span className="text-sm text-[#111111]">{t('purchase.order.inspection_fee')}</span>
                  <button onClick={() => showHelpDialog('inspection_fee')} className="cursor-pointer">
                    <img src={questionIcon} className="w-4 h-4" alt="help" />
                  </button>
                </div>
                <span className="text-sm font-medium text-[#111111]">{formatCurrency(inspectionFee)}</span>
              </div>
              <div className="flex justify-between pt-4 border-t-2 border-[#111111]">
                <span className="text-base font-bold text-[#111111]">{t('purchase.order.total')}</span>
                <div className="text-right">
                  <span className="text-xl font-bold">{formatCurrency(total)}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Order Reward Banner */}
        {hasOrderRewardTips && (
          <div className="mt-[34px]">
            <OrderRewardBanner 
              orderRewardTips={metadata.orderRewardTips}
              imageLeftPadding={0}
              imageTextGap={3}
            />
          </div>
        )}

        <div className="py-6">
          <Button
            variant="primary"
            size="xl"
            onClick={handlePay}
            disabled={!canPay || isProcessingPayment}
            className={`w-full h-14 rounded-[28px] ${
              canPay && !isProcessingPayment ? 'bg-[#111111] text-white' : 'bg-[#eeeeee] text-[#aaaaaa]'
            }`}
          >
            {isProcessingPayment ? t('purchase.payment.processing') : t('purchase.order.pay')}
          </Button>
        </div>
      </div>

      <ConfirmDialog
        isOpen={helpDialog.isOpen}
        onClose={closeHelpDialog}
        onConfirm={closeHelpDialog}
        title={helpContent.title}
        description={helpContent.description}
        confirmText={t('common.ok')}
      />
    </div>
  );
};

export default OrderSection; 
